import { z, ZodSchema, ZodUnknown } from 'zod';
import { zSchema } from '../../util/zod';

export enum WidgetsName {
    ArticleButton = 'widget-article-button',
    ArticleGallery = 'widget-article-gallery',
    ArticleTitle = 'widget-article-title',
    ArticleText = 'widget-article-text',
    ArticleImage = 'widget-article-image',
    ArticleAttachment = 'widget-article-attachment',
    ArticleSlider = 'widget-article-slider',
    CheckList = 'widget-check-list',
    DatasourceTable = 'widget-datasource-table',
    ExcelDatasourceTable = 'widget-excel-datasource-table',
    Presence = 'widget-presence',
    QuickSurvey = 'widget-quick-survey',
    ContactCardDav = 'widget-contact-cardDav',
    CalendarCalDav = 'widget-calendar-calDav',
    EmailsImap = 'widget-emails-imap',
    LuccaAbsence = 'widget-lucca-absence',
}

export const widgetKeysSchema = z.nativeEnum(WidgetsName);
export type WidgetKeys = z.infer<typeof widgetKeysSchema>;

export interface WidgetType<T> {
    name: WidgetKeys;
    uniqid: string;
    content?: T;
}
export type WidgetBaseType = {
    title: string;
    widget: WidgetType<unknown>;
};
const widgetBaseShape = z
    .object({
        title: z.string(),
        widget: z.object({
            name: widgetKeysSchema,
            uniqid: z.string(),
            content: z.unknown(),
        }),
    })
    .catchall(z.unknown());
export const widgetBaseSchema = zSchema<WidgetBaseType, typeof widgetBaseShape.shape, ZodUnknown>(widgetBaseShape);

export const cssTextAlignTypeSchema = z.enum(['left', 'right', 'center', 'justify']);
export type CSSTextAlignType = z.infer<typeof cssTextAlignTypeSchema>;

export const cssBackgroundPositionSchema = z.enum(['top', 'bottom', 'center', 'left', 'right']);
export type CSSBackgroundPosition = z.infer<typeof cssBackgroundPositionSchema>;

export const cssBackgroundSizeSchema = z.enum(['auto', 'contain', 'cover']);
export type CSSBackgroundSize = z.infer<typeof cssBackgroundSizeSchema>;

export const variantSizeTypeSchema = z.enum(['sm', 'md', 'lg']);
export type VariantSizeType = z.infer<typeof variantSizeTypeSchema>;

export const variantTypeSchema = z.enum(['contained', 'outlined']);
export type VariantType = z.infer<typeof variantTypeSchema>;

export const openingTypeSchema = z.enum(['link', 'anchor', 'popup']);
export type OpeningType = z.infer<typeof openingTypeSchema>;

export const widgetVectorSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export type WidgetVector = z.infer<typeof widgetVectorSchema>;

export const articleDisplayModeSchema = z.enum(['default', 'vertical']);
export type ArticleDisplayMode = z.infer<typeof articleDisplayModeSchema>;

export const ensureWidgetType = (widget: unknown, schema: ZodSchema, logError?: boolean) => {
    const parsedEnv = schema.safeParse(widget);
    if (parsedEnv.success === false && logError) {
        /* istanbul ignore next */
        console.error(parsedEnv.error.message);
    }
    return parsedEnv.success;
};

export const ensureWidgetContent = (content: unknown, schema: ZodSchema) => {
    return schema.safeParse(content).success;
};
